import api from "../utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "../components/ui/toast";

const CAMPAIGN_EVENTS_KEY = ["campaignEvents"];

// GET all campaign events (pass { showAll: true } to include archived)
export const useCampaignEventsQuery = (params = {}) => {
  return useQuery({
    queryKey: [...CAMPAIGN_EVENTS_KEY, params],
    queryFn: async () => {
      const res = await api.get("/admin/campaignEvents", { params });
      return res.data?.data || [];
    },
  });
};

// GET single campaign event by ID
export const useCampaignEventByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [...CAMPAIGN_EVENTS_KEY, id],
    queryFn: async () => {
      const res = await api.get(`/admin/campaignEvents/${id}`);
      return res.data?.data || null;
    },
    enabled: !!id,
    ...options,
  });
};

// CREATE campaign event
export const useAddCampaignEventsMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await api.post("/admin/campaignEvents", data);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Campaign event created successfully");
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_EVENTS_KEY });
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to create campaign event"
      );
    },
    ...options,
  });
};

// UPDATE campaign event
export const useUpdateCampaignEventsMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const res = await api.put(`/admin/campaignEvents/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Campaign event updated successfully");
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_EVENTS_KEY });
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to update campaign event"
      );
    },
    ...options,
  });
};

// RESTORE archived campaign event
export const useRestoreCampaignEventsMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await api.put(`/admin/campaignEvents/${id}/restore`);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Campaign event restored successfully");
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_EVENTS_KEY });
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to restore campaign event"
      );
    },
    ...options,
  });
};

// DELETE campaign event
export const useDeleteCampaignEventsMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await api.delete(`/admin/campaignEvents/${id}`);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Campaign event deleted successfully");
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_EVENTS_KEY });
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to delete campaign event"
      );
    },
    ...options,
  });
};
